// @ts-nocheck
import { useEffect, useState, useRef, useCallback } from "react";
import Cropper from "react-easy-crop";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { motion } from "framer-motion";
import { Plus, Trash2, Edit, Link2, Shield, Loader2, X, Phone, Unlink, Upload, Image, Search, Users, Heart, Cross, ImagePlus, CheckCircle, XCircle, Clock, KeyRound, Copy, ArrowRight, Newspaper, UserCog, ShieldCheck, BarChart3, Video, Building2, Share2, Globe, Mail, MapPin, Instagram, FileText, FolderOpen, File, Download, UserCheck, Camera, Briefcase, FileCheck } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import defaultMale from "@/assets/default-male.png";
import defaultFemale from "@/assets/default-female.png";

interface FamilyMember {
  id: string;
  name: string;
  photo_url: string | null;
  phone: string | null;
  email: string | null;
  birth_date: string | null;
  location: string | null;
  work: string | null;
  bio: string | null;
  gender: string | null;
  is_deceased: boolean;
  marital_status: string | null;
  grave_number: string | null;
  cemetery: string | null;
  grave_coordinates: string | null;
  cemetery_coordinates: string | null;
  death_date: string | null;
  member_code: string | null;
}

interface MemberClaim {
  id: string;
  user_id: string;
  member_id: string | null;
  member_code: string;
  status: string;
  created_at: string;
}

interface MemberFormData {
  name: string;
  photo_url: string;
  phones: string[];
  email: string;
  birth_date: string;
  location: string;
  work: string;
  bio: string;
  gender: string;
  is_deceased: boolean;
  marital_status: string;
  grave_number: string;
  cemetery: string;
  grave_coordinates: string;
  cemetery_coordinates: string;
  death_date: string;
}

const emptyForm: MemberFormData = {
  name: "", photo_url: "", phones: [""], email: "",
  birth_date: "", location: "", work: "", bio: "", gender: "male",
  is_deceased: false, marital_status: "unknown", grave_number: "", cemetery: "", grave_coordinates: "", cemetery_coordinates: "", death_date: "",
};

type AdminSection = "home" | "members" | "relationships" | "news" | "codes" | "gallery" | "family-users" | "admin-users" | "admin-permissions" | "statistics" | "social-media" | "businesses" | "social-channels" | "social-publish" | "member-requests" | "documents" | "documents-public" | "documents-personal" | "requests-activation" | "requests-photo" | "requests-business" | "requests-personal-doc" | "requests-public-doc";

function SearchableMemberSelect({ members, value, onChange, placeholder }: { members: FamilyMember[]; value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const selected = members.find(m => m.id === value);
  const filtered = query ? members.filter(m => m.name.includes(query)) : members;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <div
        className="flex items-center bg-secondary/50 border border-primary/20 rounded-xl px-3 py-2 cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <Search className="h-4 w-4 text-muted-foreground ml-2 shrink-0" />
        {open ? (
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder || "ابحث..."}
            className="bg-transparent outline-none text-sm flex-1 text-right"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={`text-sm flex-1 text-right ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>
            {selected?.name || placeholder || "اختر..."}
          </span>
        )}
        {value && (
          <button onClick={(e) => { e.stopPropagation(); onChange(""); setQuery(""); }} className="mr-1 text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 top-full mt-1 w-full bg-popover border border-primary/20 rounded-xl shadow-lg max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-muted-foreground text-xs text-center py-3">لا توجد نتائج</p>
          ) : (
            filtered.map(m => (
              <button
                key={m.id}
                className={`w-full text-right px-3 py-2 text-sm hover:bg-secondary/60 transition-colors ${m.id === value ? 'bg-primary/10 text-primary' : ''}`}
                onClick={() => { onChange(m.id); setOpen(false); setQuery(""); }}
              >
                {m.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const menuItems: { key: AdminSection; label: string; icon: any; color: string }[] = [
  { key: "members", label: "إدارة أفراد العائلة", icon: Users, color: "text-blue-500 bg-blue-500/10" },
  { key: "relationships", label: "إدارة ربط أفراد العائلة", icon: Link2, color: "text-emerald-500 bg-emerald-500/10" },
  { key: "news", label: "إدارة أخبار العائلة", icon: Newspaper, color: "text-amber-500 bg-amber-500/10" },
  { key: "codes", label: "أكواد العائلة", icon: KeyRound, color: "text-purple-500 bg-purple-500/10" },
  { key: "gallery", label: "إدارة معرض الصور", icon: ImagePlus, color: "text-pink-500 bg-pink-500/10" },
  { key: "documents", label: "وثائق العائلة", icon: FileText, color: "text-violet-500 bg-violet-500/10" },
  { key: "businesses", label: "شركات العائلة", icon: Building2, color: "text-indigo-500 bg-indigo-500/10" },
  { key: "social-media", label: "قنوات التواصل الاجتماعي", icon: Share2, color: "text-sky-500 bg-sky-500/10" },
  { key: "family-users", label: "إدارة المستخدمين (العائلة)", icon: Users, color: "text-teal-500 bg-teal-500/10" },
  { key: "admin-users", label: "إدارة المستخدمين (المشرف)", icon: UserCog, color: "text-orange-500 bg-orange-500/10" },
  { key: "admin-permissions", label: "صلاحيات المستخدمين (المشرف)", icon: ShieldCheck, color: "text-red-500 bg-red-500/10" },
  { key: "statistics", label: "إحصائيات", icon: BarChart3, color: "text-cyan-500 bg-cyan-500/10" },
  { key: "member-requests", label: "طلبات الأعضاء", icon: Clock, color: "text-rose-500 bg-rose-500/10" },
];

const Admin = () => {
  const { user, isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState<AdminSection>("home");

  // Reset to home when navigating to /admin
  useEffect(() => {
    setActiveSection("home");
  }, [location.key]);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [relationships, setRelationships] = useState<{ id: string; parent_id: string; child_id: string; relationship_type: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [relOpen, setRelOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MemberFormData>(emptyForm);
  const [person1Id, setPerson1Id] = useState("");
  const [person2Id, setPerson2Id] = useState("");
  const [motherId, setMotherId] = useState("");
  const [relType, setRelType] = useState("parent");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [adminSearch, setAdminSearch] = useState("");
  const [relSearch, setRelSearch] = useState("");

  // Gallery states
  const [galleryPhotos, setGalleryPhotos] = useState<any[]>([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryForm, setGalleryForm] = useState({ title: "", photo_url: "", member_ids: [] as string[] });
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [gallerySaving, setGallerySaving] = useState(false);
  const [galleryEditId, setGalleryEditId] = useState<string | null>(null);
  const [gallerySearchQuery, setGallerySearchQuery] = useState("");

  // Claims states
  const [claims, setClaims] = useState<MemberClaim[]>([]);
  const [claimProcessing, setClaimProcessing] = useState<string | null>(null);

  // News states
  const [news, setNews] = useState<any[]>([]);
  const [newsOpen, setNewsOpen] = useState(false);
  const [newsForm, setNewsForm] = useState({ title: "", content: "", image_url: "", images: [] as string[], video_url: "", news_date: new Date().toISOString().split("T")[0], main_image_index: 0 });
  const [newsSaving, setNewsSaving] = useState(false);
  const [newsUploading, setNewsUploading] = useState(false);
  const [newsVideoUploading, setNewsVideoUploading] = useState(false);
  const [editingNewsId, setEditingNewsId] = useState<string | null>(null);

  // Business states
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [businessOpen, setBusinessOpen] = useState(false);
  const [businessForm, setBusinessForm] = useState({ name_ar: "", name_en: "", logo_url: "", activity: "", location: "", country: "الإمارات العربية المتحدة", emirate: "", area: "", street: "", email: "", phone: "", website: "", instagram: "", whatsapp: "", facebook: "", twitter_x: "" });
  const [businessSaving, setBusinessSaving] = useState(false);
  const [businessEditId, setBusinessEditId] = useState<string | null>(null);
  const [businessLogoUploading, setBusinessLogoUploading] = useState(false);
  const [bizOwnerIds, setBizOwnerIds] = useState<string[]>([]);
  const [bizActivities, setBizActivities] = useState<string[]>([""]);
  const [logoCropImage, setLogoCropImage] = useState<string | null>(null);
  const [logoCrop, setLogoCrop] = useState({ x: 0, y: 0 });
  const [logoZoom, setLogoZoom] = useState(1);
  const [logoCroppedAreaPixels, setLogoCroppedAreaPixels] = useState<any>(null);
  // Social media states
  const [socialChannels, setSocialChannels] = useState<any[]>([]);
  const [socialOpen, setSocialOpen] = useState(false);
  const [socialForm, setSocialForm] = useState({ platform: "instagram", url: "", label: "" });
  const [socialSaving, setSocialSaving] = useState(false);
  const [socialEditId, setSocialEditId] = useState<string | null>(null);
  const [channelUrls, setChannelUrls] = useState<Record<string, { url: string; id: string | null }>>({
    whatsapp: { url: "", id: null },
    instagram: { url: "", id: null },
    facebook: { url: "", id: null },
    twitter_x: { url: "", id: null },
  });
  const [channelSaving, setChannelSaving] = useState<string | null>(null);

  // Member requests states
  const [memberRequests, setMemberRequests] = useState<any[]>([]);
  const [requestProcessing, setRequestProcessing] = useState<string | null>(null);

  // Documents states
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [documentsForm, setDocumentsForm] = useState({ title: "", description: "", file_url: "", doc_type: "public" as "personal" | "public", member_id: "" });
  const [documentsSaving, setDocumentsSaving] = useState(false);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [documentsEditId, setDocumentsEditId] = useState<string | null>(null);
  const [documentsTab, setDocumentsTab] = useState<"personal" | "public">("public");
  const [isLocalAdmin, setIsLocalAdmin] = useState(false);
  const [localAdminUser, setLocalAdminUser] = useState<any>(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || "";

  // Check for local admin session
  useEffect(() => {
    const checkLocalAdmin = async () => {
      const token = localStorage.getItem("admin_token");
      const savedUser = localStorage.getItem("admin_user");
      
      if (token && savedUser) {
        try {
          const response = await fetch(`${API_URL}/api/auth/verify-admin-token?token=${token}`);
          const data = await response.json();
          
          if (data.valid) {
            setIsLocalAdmin(true);
            setLocalAdminUser(data.user);
            return;
          }
        } catch (e) {
          console.log("Admin token check failed:", e);
        }
        // Clear invalid token
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
      }
      setIsLocalAdmin(false);
    };
    
    checkLocalAdmin();
  }, [API_URL]);

  useEffect(() => {
    // Allow access if local admin OR Supabase admin
    if (!isLoading && !isLocalAdmin && (!user || !isAdmin)) {
      navigate("/mgmt-x7k9z");
    }
  }, [user, isAdmin, isLoading, isLocalAdmin, navigate]);

  const fetchMembers = async () => {
    const [membersRes, relsRes, galleryRes, claimsRes, newsRes, bizRes, socialRes, requestsRes, docsRes] = await Promise.all([
      supabase.from("family_members").select("*").order("name"),
      supabase.from("family_relationships").select("*"),
      supabase.from("gallery_photos").select("*").order("created_at", { ascending: false }),
      supabase.from("member_claims").select("*").order("created_at", { ascending: false }),
      supabase.from("family_news").select("*").order("created_at", { ascending: false }),
      supabase.from("family_businesses").select("*").order("created_at", { ascending: false }),
      supabase.from("social_media_channels").select("*").order("created_at", { ascending: false }),
      supabase.from("member_requests").select("*").order("created_at", { ascending: false }),
      supabase.from("family_documents").select("*").order("created_at", { ascending: false }),
    ]);
    setMembers((membersRes.data as unknown as FamilyMember[]) || []);
    setRelationships((relsRes.data as any[]) || []);
    setGalleryPhotos((galleryRes.data as any[]) || []);
    setClaims((claimsRes.data as unknown as MemberClaim[]) || []);
    setNews((newsRes.data as any[]) || []);
    setBusinesses((bizRes.data as any[]) || []);
    setSocialChannels((socialRes.data as any[]) || []);
    setMemberRequests((requestsRes.data as any[]) || []);
    setDocuments((docsRes.data as any[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  // Sync channel URLs from socialChannels
  useEffect(() => {
    const urls: Record<string, { url: string; id: string | null }> = {
      whatsapp: { url: "", id: null },
      instagram: { url: "", id: null },
      facebook: { url: "", id: null },
      twitter_x: { url: "", id: null },
    };
    socialChannels.forEach((ch: any) => {
      if (urls[ch.platform] !== undefined) {
        urls[ch.platform] = { url: ch.url, id: ch.id };
      }
    });
    setChannelUrls(urls);
  }, [socialChannels]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: "الاسم مطلوب", variant: "destructive" });
      return;
    }
    setSaving(true);
    const phoneStr = form.phones.filter(p => p.trim()).join(" | ");
    const payload = {
      name: form.name,
      photo_url: form.photo_url || null,
      phone: phoneStr || null,
      email: form.email || null,
      birth_date: form.birth_date || null,
      location: form.location || null,
      work: form.work || null,
      bio: form.bio || null,
      gender: form.gender || "male",
      is_deceased: form.is_deceased,
      marital_status: form.marital_status || "unknown",
      grave_number: form.is_deceased ? (form.grave_number || null) : null,
      cemetery: form.is_deceased ? (form.cemetery || null) : null,
      grave_coordinates: form.is_deceased ? (form.grave_coordinates || null) : null,
      cemetery_coordinates: form.is_deceased ? (form.cemetery_coordinates || null) : null,
      death_date: form.is_deceased ? (form.death_date || null) : null,
    };

    let error;
    if (editingId) {
      ({ error } = await supabase.from("family_members").update(payload).eq("id", editingId));
    } else {
      ({ error } = await supabase.from("family_members").insert(payload));
    }

    setSaving(false);
    if (error) {
      toast({ title: "حدث خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editingId ? "تم التعديل" : "تمت الإضافة" });
      setFormOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      fetchMembers();
    }
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("family_members").delete().eq("id", id);
    if (!error) {
      toast({ title: "تم الحذف" });
      fetchMembers();
    }
  };

  const handleAddRelation = async () => {
    if (relType === "parent") {
      if (!person1Id || !motherId || !person2Id || person1Id === person2Id || motherId === person2Id) {
        toast({ title: "اختر الأب والأم والابن/الابنة بشكل صحيح", variant: "destructive" });
        return;
      }
      const { error: e1 } = await supabase.from("family_relationships").insert({ parent_id: person1Id, child_id: person2Id, relationship_type: "parent" });
      const { error: e2 } = await supabase.from("family_relationships").insert({ parent_id: motherId, child_id: person2Id, relationship_type: "parent" });
      if (e1 || e2) {
        toast({ title: "خطأ", description: (e1 || e2)?.message, variant: "destructive" });
      } else {
        toast({ title: "تمت إضافة العلاقة (أب + أم)" });
        setRelOpen(false);
        setPerson1Id(""); setPerson2Id(""); setMotherId("");
        fetchMembers();
      }
    } else {
      if (!person1Id || !person2Id || person1Id === person2Id) {
        toast({ title: "اختر الشخصين بشكل صحيح", variant: "destructive" });
        return;
      }
      const { error } = await supabase.from("family_relationships").insert({ parent_id: person1Id, child_id: person2Id, relationship_type: "spouse" });
      if (error) {
        toast({ title: "خطأ", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "تمت إضافة العلاقة" });
        setRelOpen(false);
        setPerson1Id(""); setPerson2Id(""); setMotherId("");
        fetchMembers();
      }
    }
  };

  const openEdit = (m: FamilyMember) => {
    setEditingId(m.id);
    const phones = m.phone ? m.phone.split(" | ") : [""];
    setForm({
      name: m.name,
      photo_url: m.photo_url || "",
      phones,
      email: m.email || "",
      birth_date: m.birth_date || "",
      location: m.location || "",
      work: m.work || "",
      bio: m.bio || "",
      gender: m.gender || "male",
      is_deceased: m.is_deceased ?? false,
      marital_status: m.marital_status || "unknown",
      grave_number: m.grave_number || "",
      cemetery: m.cemetery || "",
      grave_coordinates: m.grave_coordinates || "",
      cemetery_coordinates: (m as any).cemetery_coordinates || "",
      death_date: (m as any).death_date || "",
    });
    setFormOpen(true);
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "يرجى اختيار ملف صورة", variant: "destructive" });
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("member-photos").upload(fileName, file);
    if (error) {
      toast({ title: "خطأ في رفع الصورة", description: error.message, variant: "destructive" });
    } else {
      const { data: urlData } = supabase.storage.from("member-photos").getPublicUrl(fileName);
      setForm(f => ({ ...f, photo_url: urlData.publicUrl }));
      toast({ title: "تم رفع الصورة بنجاح" });
    }
    setUploading(false);
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGalleryUploading(true);
    const ext = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("gallery-photos").upload(fileName, file);
    if (error) {
      toast({ title: "خطأ في رفع الصورة", description: error.message, variant: "destructive" });
    } else {
      const { data: urlData } = supabase.storage.from("gallery-photos").getPublicUrl(fileName);
      setGalleryForm(f => ({ ...f, photo_url: urlData.publicUrl }));
      toast({ title: "تم رفع الصورة" });
    }
    setGalleryUploading(false);
  };

  const handleGallerySave = async () => {
    if (!galleryForm.photo_url) {
      toast({ title: "الصورة مطلوبة", variant: "destructive" });
      return;
    }
    setGallerySaving(true);

    if (galleryEditId) {
      // Update existing photo
      const { error } = await supabase.from("gallery_photos").update({
        title: galleryForm.title || null,
        photo_url: galleryForm.photo_url,
        member_id: galleryForm.member_ids.length > 0 ? galleryForm.member_ids[0] : null,
      }).eq("id", galleryEditId);
      setGallerySaving(false);
      if (error) {
        toast({ title: "خطأ", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "تم تحديث الصورة" });
        setGalleryOpen(false);
        setGalleryForm({ title: "", photo_url: "", member_ids: [] });
        setGalleryEditId(null);
        fetchMembers();
      }
    } else {
      // Insert new
      const memberIds = galleryForm.member_ids.length > 0 ? galleryForm.member_ids : [null];
      const rows = memberIds.map(mid => ({
        title: galleryForm.title || null,
        description: null,
        photo_url: galleryForm.photo_url,
        member_id: mid,
        category: "general",
      }));
      const { error } = await supabase.from("gallery_photos").insert(rows);
      setGallerySaving(false);
      if (error) {
        toast({ title: "خطأ", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "تمت إضافة الصورة للمعرض" });
        setGalleryOpen(false);
        setGalleryForm({ title: "", photo_url: "", member_ids: [] });
        fetchMembers();
      }
    }
  };

  const handleGalleryDelete = async (id: string) => {
    const { error } = await supabase.from("gallery_photos").delete().eq("id", id);
    if (!error) { toast({ title: "تم حذف الصورة" }); fetchMembers(); }
  };

  const handleApproveClaim = async (claimId: string) => {
    setClaimProcessing(claimId);
    const { error } = await supabase.rpc("approve_member_claim", { claim_id: claimId });
    setClaimProcessing(null);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تمت الموافقة على الطلب" });
      fetchMembers();
    }
  };

  const handleRejectClaim = async (claimId: string) => {
    setClaimProcessing(claimId);
    const { error } = await supabase.from("member_claims").update({ status: "rejected", reviewed_at: new Date().toISOString() }).eq("id", claimId);
    setClaimProcessing(null);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم رفض الطلب" });
      fetchMembers();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "تم النسخ" });
  };

  const addPhone = () => setForm(f => ({ ...f, phones: [...f.phones, ""] }));
  const removePhone = (i: number) => setForm(f => ({ ...f, phones: f.phones.filter((_, idx) => idx !== i) }));
  const updatePhone = (i: number, val: string) => setForm(f => ({ ...f, phones: f.phones.map((p, idx) => idx === i ? val : p) }));

  // News handlers
  const handleNewsImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setNewsUploading(true);
    const newUrls: string[] = [];
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop();
      const fileName = `news-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error } = await supabase.storage.from("gallery-photos").upload(fileName, file);
      if (error) {
        toast({ title: "خطأ في رفع الصورة", description: error.message, variant: "destructive" });
      } else {
        const { data: urlData } = supabase.storage.from("gallery-photos").getPublicUrl(fileName);
        newUrls.push(urlData.publicUrl);
      }
    }
    if (newUrls.length) {
      setNewsForm(f => ({ ...f, images: [...f.images, ...newUrls] }));
      toast({ title: `تم رفع ${newUrls.length} صورة` });
    }
    setNewsUploading(false);
  };

  const handleNewsVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast({ title: "يرجى اختيار ملف فيديو", variant: "destructive" });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "حجم الفيديو يجب أن لا يتجاوز 50 ميجابايت", variant: "destructive" });
      return;
    }
    setNewsVideoUploading(true);
    const ext = file.name.split(".").pop();
    const fileName = `video-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("news-videos").upload(fileName, file);
    if (error) {
      toast({ title: "خطأ في رفع الفيديو", description: error.message, variant: "destructive" });
    } else {
      const { data: urlData } = supabase.storage.from("news-videos").getPublicUrl(fileName);
      setNewsForm(f => ({ ...f, video_url: urlData.publicUrl }));
      toast({ title: "تم رفع الفيديو" });
    }
    setNewsVideoUploading(false);
  };

  const removeNewsImage = (index: number) => {
    setNewsForm(f => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  };

  const handleNewsSave = async () => {
    if (!newsForm.title.trim() || !newsForm.content.trim()) {
      toast({ title: "العنوان والمحتوى مطلوبان", variant: "destructive" });
      return;
    }
    setNewsSaving(true);
    const mainImg = newsForm.images[newsForm.main_image_index] || newsForm.images[0] || newsForm.image_url || null;
    const payload: any = {
      title: newsForm.title,
      content: newsForm.content,
      image_url: mainImg,
      images: newsForm.images,
      video_url: newsForm.video_url || null,
      news_date: newsForm.news_date || null,
    };
    let error;
    if (editingNewsId) {
      ({ error } = await supabase.from("family_news").update(payload).eq("id", editingNewsId));
    } else {
      ({ error } = await supabase.from("family_news").insert(payload));
    }
    setNewsSaving(false);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: editingNewsId ? "تم تعديل الخبر" : "تمت إضافة الخبر" });
      setNewsOpen(false);
      setEditingNewsId(null);
      setNewsForm({ title: "", content: "", image_url: "", images: [], video_url: "", news_date: new Date().toISOString().split("T")[0], main_image_index: 0 });
      fetchMembers();
    }
  };

  const handleNewsDelete = async (id: string) => {
    const { error } = await supabase.from("family_news").delete().eq("id", id);
    if (!error) { toast({ title: "تم حذف الخبر" }); fetchMembers(); }
  };

  const openEditNews = (n: any) => {
    setEditingNewsId(n.id);
    const imgs = n.images || [];
    const mainIdx = n.image_url ? imgs.indexOf(n.image_url) : 0;
    setNewsForm({ title: n.title, content: n.content, image_url: n.image_url || "", images: imgs, video_url: n.video_url || "", news_date: n.news_date || new Date().toISOString().split("T")[0], main_image_index: mainIdx >= 0 ? mainIdx : 0 });
    setNewsOpen(true);
  };

  if (isLoading) return null;

  const SectionHeader = ({ title, onBack }: { title: string; onBack: () => void }) => (
    <div className="flex items-center gap-3 mb-6">
      <Button variant="ghost" size="icon" onClick={onBack} className="rounded-xl">
        <ArrowRight className="h-5 w-5" />
      </Button>
      <h2 className="font-amiri text-2xl gold-text font-bold">{title}</h2>
    </div>
  );

  // =================== HOME MENU ===================
  const renderHome = () => (
    <>
      {/* Menu buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {menuItems.map((item, i) => (
          <motion.div
            key={item.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <button
              onClick={() => setActiveSection(item.key)}
              className="w-full flex items-center gap-4 p-5 rounded-2xl glass-card border border-primary/10 hover:border-primary/30 transition-all hover:shadow-lg group text-right"
            >
              <div className={`p-3 rounded-xl ${item.color} transition-transform group-hover:scale-110`}>
                <item.icon className="h-6 w-6" />
              </div>
              <span className="font-medium text-foreground text-base flex-1">{item.label}</span>
              <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors rotate-180" />
            </button>
          </motion.div>
        ))}
      </div>
    </>
  );

  // =================== MEMBERS SECTION ===================
  const renderMembers = () => (
    <>
      <SectionHeader title="إدارة أفراد العائلة" onBack={() => setActiveSection("home")} />
      
      <div className="flex gap-3 mb-6 justify-center flex-wrap">
        <Dialog open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button className="gold-gradient text-primary-foreground rounded-xl shadow-lg">
              <Plus className="h-4 w-4 ml-1" /> إضافة فرد
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-card border-primary/20 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" dir="rtl">
            <DialogHeader>
              <DialogTitle className="font-amiri text-xl gold-text">
                {editingId ? "تعديل فرد" : "إضافة فرد جديد"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>الاسم *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>الجنس</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger className="bg-secondary/50 border-primary/20 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">ذكر</SelectItem>
                      <SelectItem value="female">أنثى</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>الحالة الاجتماعية</Label>
                  <Select value={form.marital_status} onValueChange={(v) => setForm({ ...form, marital_status: v })}>
                    <SelectTrigger className="bg-secondary/50 border-primary/20 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">غير محدد</SelectItem>
                      <SelectItem value="single">أعزب/عزباء</SelectItem>
                      <SelectItem value="married">متزوج/ة</SelectItem>
                      <SelectItem value="divorced">مطلق/ة</SelectItem>
                      <SelectItem value="widowed">أرمل/ة</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-3 pb-1">
                  <div className="flex items-center gap-2">
                    <Switch checked={form.is_deceased} onCheckedChange={(v) => setForm({ ...form, is_deceased: v })} />
                    <Label className="text-sm">متوفي</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>صورة الفرد</Label>
                <div className="flex items-center gap-3">
                  <Avatar className="h-14 w-14 border-2 border-primary/20">
                    <AvatarImage src={form.photo_url || (form.gender === "female" ? defaultFemale : defaultMale)} className="object-contain scale-[0.78]" />
                    <AvatarFallback className="bg-secondary">
                      <img src={form.gender === "female" ? defaultFemale : defaultMale} className="w-full h-full object-contain scale-[0.78]" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-primary/20 bg-secondary/50 cursor-pointer hover:bg-secondary/70 transition-colors">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Upload className="h-4 w-4 text-primary" />}
                      <span className="text-sm text-muted-foreground">{uploading ? "جاري الرفع..." : "رفع صورة من الجهاز"}</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
                    </label>
                    <Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} dir="ltr" placeholder="أو أدخل رابط الصورة" className="bg-secondary/50 border-primary/20 rounded-xl text-xs h-8" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>أرقام الهاتف</Label>
                  <Button type="button" variant="ghost" size="sm" onClick={addPhone} className="text-primary text-xs">
                    <Plus className="h-3 w-3 ml-1" /> إضافة رقم
                  </Button>
                </div>
                {form.phones.map((phone, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Input value={phone} onChange={(e) => updatePhone(i, e.target.value)} dir="ltr" placeholder={`رقم ${i + 1}`} className="bg-secondary/50 border-primary/20 rounded-xl" />
                    {form.phones.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(i)} className="shrink-0 h-8 w-8">
                        <X className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>البريد</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} dir="ltr" className="bg-secondary/50 border-primary/20 rounded-xl" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>تاريخ الميلاد</Label>
                  <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} dir="ltr" className="bg-secondary/50 border-primary/20 rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>مكان الإقامة</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>العمل</Label>
                <Input value={form.work} onChange={(e) => setForm({ ...form, work: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>السيرة الذاتية</Label>
                <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
              </div>

              {form.is_deceased && (
                <div className="space-y-4 p-3 rounded-xl border border-destructive/20 bg-destructive/5">
                  <div className="space-y-2">
                    <Label>تاريخ الوفاة</Label>
                    <Input type="date" value={form.death_date} onChange={(e) => setForm({ ...form, death_date: e.target.value })} dir="ltr" className="bg-secondary/50 border-primary/20 rounded-xl" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>المقبرة</Label>
                      <Input value={form.cemetery} onChange={(e) => setForm({ ...form, cemetery: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label>رقم القبر</Label>
                      <Input value={form.grave_number} onChange={(e) => setForm({ ...form, grave_number: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>رابط موقع المقبرة (خرائط جوجل)</Label>
                    <Input value={form.cemetery_coordinates} onChange={(e) => setForm({ ...form, cemetery_coordinates: e.target.value })} dir="ltr" placeholder="https://maps.google.com/..." className="bg-secondary/50 border-primary/20 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label>رابط موقع القبر (خرائط جوجل)</Label>
                    <Input value={form.grave_coordinates} onChange={(e) => setForm({ ...form, grave_coordinates: e.target.value })} dir="ltr" placeholder="https://maps.google.com/..." className="bg-secondary/50 border-primary/20 rounded-xl" />
                  </div>
                </div>
              )}
              <Button onClick={handleSave} className="w-full gold-gradient text-primary-foreground rounded-xl" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : null}
                {editingId ? "حفظ التعديلات" : "إضافة"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="font-amiri text-xl text-primary">أفراد العائلة ({members.length})</CardTitle>
            <div className="relative w-full max-w-xs">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input value={adminSearch} onChange={(e) => setAdminSearch(e.target.value)} placeholder="ابحث بالاسم..." className="pr-9 pl-9 text-right bg-secondary/50 border-primary/20 rounded-xl" dir="rtl" />
              {adminSearch && (
                <button onClick={() => setAdminSearch("")} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-center py-8">جاري التحميل...</p>
          ) : members.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">لا يوجد أفراد بعد، أضف أول فرد!</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-primary/10">
                    <TableHead className="text-right text-primary">الاسم</TableHead>
                    <TableHead className="text-right text-primary">الجنس</TableHead>
                    <TableHead className="text-right text-primary">الحالة</TableHead>
                    <TableHead className="text-right text-primary">الجوال</TableHead>
                    <TableHead className="text-right text-primary">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.filter(m => m.name.includes(adminSearch)).map((m) => (
                    <TableRow key={m.id} className="border-primary/10">
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {m.name}
                          {m.is_deceased && <Badge variant="outline" className="text-xs border-destructive/50 text-destructive">متوفي</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>{m.gender === "female" ? "أنثى" : "ذكر"}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {m.marital_status === "married" ? "متزوج/ة" :
                           m.marital_status === "single" ? "أعزب/عزباء" :
                           m.marital_status === "divorced" ? "مطلق/ة" :
                           m.marital_status === "widowed" ? "أرمل/ة" : "—"}
                        </Badge>
                      </TableCell>
                      <TableCell dir="ltr" className="text-left">{m.phone || "—"}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(m)} className="h-8 w-8">
                            <Edit className="h-4 w-4 text-primary" />
                          </Button>
                          <Button size="icon" variant="ghost" onClick={() => handleDelete(m.id)} className="h-8 w-8">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  // =================== RELATIONSHIPS SECTION ===================
  const renderRelationships = () => (
    <>
      <SectionHeader title="إدارة ربط أفراد العائلة" onBack={() => setActiveSection("home")} />
      
      <div className="flex gap-3 mb-6 justify-center">
        <Dialog open={relOpen} onOpenChange={setRelOpen}>
          <DialogTrigger asChild>
            <Button className="gold-gradient text-primary-foreground rounded-xl shadow-lg">
              <Plus className="h-4 w-4 ml-1" /> إضافة علاقة
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-card border-primary/20 max-w-md rounded-2xl" dir="rtl">
            <DialogHeader>
              <DialogTitle className="font-amiri text-xl gold-text">إضافة علاقة</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>نوع العلاقة</Label>
                <Select value={relType} onValueChange={setRelType}>
                  <SelectTrigger className="bg-secondary/50 border-primary/20 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">أب/أم ← ابن/ابنة</SelectItem>
                    <SelectItem value="spouse">زوج ↔ زوجة</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{relType === "spouse" ? "الزوج" : "الأب"}</Label>
                <SearchableMemberSelect members={members} value={person1Id} onChange={setPerson1Id} placeholder="ابحث واختر..." />
              </div>
              {relType === "parent" && (
                <div className="space-y-2">
                  <Label>الأم</Label>
                  <SearchableMemberSelect members={members} value={motherId} onChange={setMotherId} placeholder="ابحث واختر الأم..." />
                </div>
              )}
              <div className="space-y-2">
                <Label>{relType === "spouse" ? "الزوجة" : "الابن/الابنة"}</Label>
                <SearchableMemberSelect members={members} value={person2Id} onChange={setPerson2Id} placeholder="ابحث واختر..." />
              </div>
              <Button onClick={handleAddRelation} className="w-full gold-gradient text-primary-foreground rounded-xl">إضافة العلاقة</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="font-amiri text-xl text-primary flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              الروابط العائلية ({relationships.length})
            </CardTitle>
            <div className="relative w-full max-w-xs">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input value={relSearch} onChange={(e) => setRelSearch(e.target.value)} placeholder="ابحث بالاسم..." className="pr-9 pl-9 text-right bg-secondary/50 border-primary/20 rounded-xl" dir="rtl" />
              {relSearch && (
                <button onClick={() => setRelSearch("")} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {relationships.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">لا توجد روابط بعد</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-primary/10">
                    <TableHead className="text-right text-primary">الشخص الأول</TableHead>
                    <TableHead className="text-right text-primary">نوع العلاقة</TableHead>
                    <TableHead className="text-right text-primary">الشخص الثاني</TableHead>
                    <TableHead className="text-right text-primary">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {relationships.filter(rel => {
                    if (!relSearch) return true;
                    const p1 = members.find(m => m.id === rel.parent_id);
                    const p2 = members.find(m => m.id === rel.child_id);
                    return (p1?.name || "").includes(relSearch) || (p2?.name || "").includes(relSearch);
                  }).map((rel) => {
                    const p1 = members.find(m => m.id === rel.parent_id);
                    const p2 = members.find(m => m.id === rel.child_id);
                    return (
                      <TableRow key={rel.id} className="border-primary/10">
                        <TableCell className="font-medium">{p1?.name || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {rel.relationship_type === "spouse" ? "زوج ↔ زوجة" : "أب/أم ← ابن/ابنة"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{p2?.name || "—"}</TableCell>
                        <TableCell>
                          <Button size="icon" variant="ghost" onClick={async () => {
                            const { error } = await supabase.from("family_relationships").delete().eq("id", rel.id);
                            if (!error) { toast({ title: "تم حذف العلاقة" }); fetchMembers(); }
                          }} className="h-8 w-8">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unlinked members */}
      {(() => {
        const linkedIds = new Set(relationships.flatMap(r => [r.parent_id, r.child_id]));
        const unlinked = members.filter(m => !linkedIds.has(m.id));
        if (unlinked.length === 0) return null;
        return (
          <Card className="glass-card border-destructive/20 rounded-2xl overflow-hidden mt-6">
            <CardHeader>
              <CardTitle className="font-amiri text-xl text-destructive flex items-center gap-2">
                <Unlink className="h-5 w-5" />
                أفراد بدون ربط ({unlinked.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {unlinked.map(m => (
                  <Badge key={m.id} variant="outline" className="text-sm border-destructive/30 text-destructive px-3 py-1">
                    {m.name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </>
  );

  // =================== NEWS SECTION ===================
  const renderNews = () => (
    <>
      <SectionHeader title="إدارة أخبار العائلة" onBack={() => setActiveSection("home")} />
      
      <div className="flex gap-3 mb-6 justify-center">
        <Dialog open={newsOpen} onOpenChange={(o) => { setNewsOpen(o); if (!o) { setEditingNewsId(null); setNewsForm({ title: "", content: "", image_url: "", images: [], video_url: "", news_date: new Date().toISOString().split("T")[0], main_image_index: 0 }); } }}>
          <DialogTrigger asChild>
            <Button className="gold-gradient text-primary-foreground rounded-xl shadow-lg">
              <Plus className="h-4 w-4 ml-1" /> إضافة خبر
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-card border-primary/20 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" dir="rtl">
            <DialogHeader>
              <DialogTitle className="font-amiri text-xl gold-text">
                {editingNewsId ? "تعديل الخبر" : "إضافة خبر جديد"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>العنوان *</Label>
                <Input value={newsForm.title} onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>تاريخ الخبر *</Label>
                <Input type="date" value={newsForm.news_date} onChange={(e) => setNewsForm({ ...newsForm, news_date: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>المحتوى *</Label>
                <Textarea value={newsForm.content} onChange={(e) => setNewsForm({ ...newsForm, content: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl min-h-[120px]" />
              </div>
              <div className="space-y-2">
                <Label>الصور (اختياري)</Label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-primary/20 bg-secondary/50 cursor-pointer hover:bg-secondary/70 transition-colors">
                  {newsUploading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <ImagePlus className="h-4 w-4 text-primary" />}
                  <span className="text-sm text-muted-foreground">{newsUploading ? "جاري الرفع..." : "رفع صور"}</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleNewsImageUpload} disabled={newsUploading} />
                </label>
                {newsForm.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {newsForm.images.map((url, i) => (
                      <div key={i} className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 ${newsForm.main_image_index === i ? 'border-primary' : 'border-transparent'}`} onClick={() => setNewsForm(f => ({ ...f, main_image_index: i }))}>
                        <img src={url} alt={`صورة ${i + 1}`} className="w-full h-20 object-cover" />
                        {newsForm.main_image_index === i && (
                          <div className="absolute bottom-1 right-1 bg-primary text-primary-foreground text-[10px] px-1.5 py-0.5 rounded-full font-bold">رئيسية</div>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); removeNewsImage(i); }}
                          className="absolute top-1 left-1 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {newsForm.images.length > 1 && (
                  <p className="text-xs text-muted-foreground mt-1">اضغط على الصورة لتحديدها كصورة رئيسية</p>
                )}
              </div>
              <div className="space-y-2">
                <Label>فيديو (اختياري)</Label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-primary/20 bg-secondary/50 cursor-pointer hover:bg-secondary/70 transition-colors">
                  {newsVideoUploading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Video className="h-4 w-4 text-primary" />}
                  <span className="text-sm text-muted-foreground">{newsVideoUploading ? "جاري رفع الفيديو..." : "رفع فيديو"}</span>
                  <input type="file" accept="video/*" className="hidden" onChange={handleNewsVideoUpload} disabled={newsVideoUploading} />
                </label>
                {newsForm.video_url && (
                  <div className="relative mt-2">
                    <video src={newsForm.video_url} className="w-full max-h-40 rounded-xl" controls />
                    <button
                      type="button"
                      onClick={() => setNewsForm(f => ({ ...f, video_url: "" }))}
                      className="absolute top-1 left-1 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
              <Button onClick={handleNewsSave} className="w-full gold-gradient text-primary-foreground rounded-xl" disabled={newsSaving}>
                {newsSaving ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : null}
                {editingNewsId ? "حفظ التعديلات" : "إضافة الخبر"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardHeader>
          <CardTitle className="font-amiri text-xl text-primary flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            الأخبار ({news.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {news.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">لا توجد أخبار بعد</p>
          ) : (
            <div className="space-y-3">
              {news.map(n => (
                <div key={n.id} className="flex items-start justify-between p-4 rounded-xl bg-secondary/30 border border-primary/10 gap-4">
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground mb-1">{n.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{n.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">{new Date(n.created_at).toLocaleDateString("ar")}</p>
                  </div>
                  {n.image_url && (
                    <img src={n.image_url} alt={n.title} className="w-20 h-20 object-cover rounded-xl shrink-0" />
                  )}
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEditNews(n)} className="h-8 w-8">
                      <Edit className="h-4 w-4 text-primary" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={async () => {
                      toast({ title: "جارٍ إرسال البريد..." });
                      const { data: { session } } = await supabase.auth.getSession();
                      const res = await supabase.functions.invoke("send-news-email", {
                        body: { news_id: n.id },
                      });
                      if (res.error) {
                        toast({ title: "فشل الإرسال", description: res.error.message, variant: "destructive" });
                      } else {
                        toast({ title: `تم الإرسال إلى ${res.data?.recipients || 0} عضو` });
                      }
                    }} className="h-8 w-8" title="إرسال بالبريد لأفراد العائلة">
                      <Mail className="h-4 w-4 text-accent" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleNewsDelete(n.id)} className="h-8 w-8">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  // =================== CODES SECTION ===================
  const renderCodes = () => (
    <>
      <SectionHeader title="أكواد العائلة" onBack={() => setActiveSection("home")} />
      
      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardHeader>
          <CardTitle className="font-amiri text-xl text-primary flex items-center gap-2">
            <Copy className="h-5 w-5" />
            أكواد الأعضاء
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {members.filter(m => m.member_code).map(m => (
              <div key={m.id} className="flex items-center justify-between p-2.5 rounded-xl bg-secondary/30 border border-primary/10">
                <span className="text-sm truncate flex-1">{m.name}</span>
                <button
                  onClick={() => copyToClipboard(m.member_code!)}
                  className="font-mono text-xs bg-primary/10 text-primary px-2.5 py-1 rounded-lg hover:bg-primary/20 transition-colors tracking-wider"
                >
                  {m.member_code}
                </button>
              </div>
            ))}
          </div>
          {members.filter(m => m.member_code).length === 0 && (
            <p className="text-muted-foreground text-center py-6">لا توجد أكواد بعد</p>
          )}
        </CardContent>
      </Card>
    </>
  );

  // =================== GALLERY SECTION ===================
  const renderGallery = () => (
    <>
      <SectionHeader title="إدارة معرض الصور" onBack={() => setActiveSection("home")} />
      
      <div className="flex gap-3 mb-6 justify-center">
        <Dialog open={galleryOpen} onOpenChange={(o) => { setGalleryOpen(o); if (!o) { setGalleryForm({ title: "", photo_url: "", member_ids: [] }); setGalleryEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="gold-gradient text-primary-foreground rounded-xl shadow-lg">
              <Plus className="h-4 w-4 ml-1" /> إضافة صورة
            </Button>
          </DialogTrigger>
           <DialogContent className="glass-card border-primary/20 max-w-md max-h-[90vh] overflow-y-auto rounded-2xl" dir="rtl">
            <DialogHeader>
              <DialogTitle className="font-amiri text-xl gold-text">{galleryEditId ? "تعديل الصورة" : "إضافة صورة للمعرض"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>الصورة *</Label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-xl border border-primary/20 bg-secondary/50 cursor-pointer hover:bg-secondary/70 transition-colors">
                  {galleryUploading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <Upload className="h-4 w-4 text-primary" />}
                  <span className="text-sm text-muted-foreground">{galleryUploading ? "جاري الرفع..." : "رفع صورة"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleGalleryUpload} disabled={galleryUploading} />
                </label>
                {galleryForm.photo_url && (
                  <img src={galleryForm.photo_url} alt="preview" className="w-full max-h-40 object-cover rounded-xl mt-2" />
                )}
              </div>
              <div className="space-y-2">
                <Label>العنوان</Label>
                <Input value={galleryForm.title} onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })} className="bg-secondary/50 border-primary/20 rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>أفراد العائلة (اختياري - يمكن اختيار أكثر من فرد)</Label>
                <SearchableMemberSelect members={members} value={galleryForm.member_ids[galleryForm.member_ids.length - 1] || ""} onChange={(v) => {
                  if (v && !galleryForm.member_ids.includes(v)) {
                    setGalleryForm({ ...galleryForm, member_ids: [...galleryForm.member_ids, v] });
                  }
                }} placeholder="اختر فرد لإضافته..." />
                {galleryForm.member_ids.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {galleryForm.member_ids.map(mid => {
                      const m = members.find(x => x.id === mid);
                      return (
                        <Badge key={mid} variant="secondary" className="text-xs px-2.5 py-1 gap-1">
                          {m?.name || mid}
                          <button onClick={() => setGalleryForm(f => ({ ...f, member_ids: f.member_ids.filter(id => id !== mid) }))} className="hover:text-destructive">
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
               <Button onClick={handleGallerySave} className="w-full gold-gradient text-primary-foreground rounded-xl" disabled={gallerySaving}>
                {gallerySaving ? <Loader2 className="h-4 w-4 animate-spin ml-2" /> : null}
                {galleryEditId ? "حفظ التعديلات" : "إضافة للمعرض"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardHeader>
          <CardTitle className="font-amiri text-xl text-primary flex items-center gap-2">
            <ImagePlus className="h-5 w-5" />
            معرض الصور ({galleryPhotos.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search by name */}
          <div className="mb-4">
            <div className="relative max-w-sm mx-auto">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="ابحث بالاسم أو العنوان..."
                value={gallerySearchQuery}
                onChange={(e) => setGallerySearchQuery(e.target.value)}
                className="bg-secondary/50 border-primary/20 rounded-xl pr-9 text-right"
                dir="rtl"
              />
            </div>
          </div>
          {galleryPhotos.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">لا توجد صور في المعرض بعد</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {galleryPhotos.filter(p => {
                if (!gallerySearchQuery.trim()) return true;
                const q = gallerySearchQuery.trim().toLowerCase();
                const memberName = members.find(m => m.id === p.member_id)?.name?.toLowerCase() || "";
                const title = (p.title || "").toLowerCase();
                return memberName.includes(q) || title.includes(q);
              }).map(p => {
                const memberName = members.find(m => m.id === p.member_id)?.name;
                return (
                  <div key={p.id} className="rounded-xl overflow-hidden border border-primary/10 bg-secondary/30">
                    <div className="relative group bg-black/5 flex items-center justify-center">
                      <img src={p.photo_url} alt={p.title || "صورة"} className="w-full h-auto max-h-52 object-contain" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {p.title && <p className="text-sm font-medium text-foreground truncate">{p.title}</p>}
                          {memberName && <p className="text-xs text-muted-foreground mt-0.5">{memberName}</p>}
                          {!p.title && !memberName && <p className="text-xs text-muted-foreground">بدون عنوان</p>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="icon" variant="outline" className="h-8 w-8 border-primary/20 hover:bg-primary/10" onClick={() => {
                            setGalleryEditId(p.id);
                            setGalleryForm({
                              title: p.title || "",
                              photo_url: p.photo_url,
                              member_ids: p.member_id ? [p.member_id] : [],
                            });
                            setGalleryOpen(true);
                          }}>
                            <Edit className="h-3.5 w-3.5 text-primary" />
                          </Button>
                          <Button size="icon" variant="outline" className="h-8 w-8 border-destructive/20 hover:bg-destructive/10" onClick={() => handleGalleryDelete(p.id)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  // =================== FAMILY USERS (CLAIMS) SECTION ===================
  const renderFamilyUsers = () => (
    <>
      <SectionHeader title="إدارة المستخدمين (العائلة)" onBack={() => setActiveSection("home")} />
      
      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardHeader>
          <CardTitle className="font-amiri text-xl text-primary flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            طلبات المطالبة بالحسابات
            {claims.filter(c => c.status === "pending").length > 0 && (
              <Badge variant="destructive" className="text-xs">{claims.filter(c => c.status === "pending").length} جديد</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {claims.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">لا توجد طلبات مطالبة</p>
          ) : (
            <div className="space-y-3">
              {claims.map(claim => {
                const member = members.find(m => m.id === claim.member_id);
                return (
                  <div key={claim.id} className="flex items-center justify-between p-3 rounded-xl bg-secondary/30 border border-primary/10">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${claim.status === "pending" ? "bg-amber-500/10" : claim.status === "approved" ? "bg-green-500/10" : "bg-destructive/10"}`}>
                        {claim.status === "pending" ? <Clock className="h-4 w-4 text-amber-500" /> : claim.status === "approved" ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-destructive" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{member?.name || "غير معروف"}</p>
                        <p className="text-xs text-muted-foreground">الكود: <span className="font-mono">{claim.member_code}</span></p>
                        <p className="text-xs text-muted-foreground">{new Date(claim.created_at).toLocaleDateString("ar")}</p>
                      </div>
                    </div>
                    {claim.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="text-green-500 border-green-500/30 hover:bg-green-500/10 rounded-lg" disabled={claimProcessing === claim.id} onClick={() => handleApproveClaim(claim.id)}>
                          {claimProcessing === claim.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 ml-1" />}
                          موافقة
                        </Button>
                        <Button size="sm" variant="outline" className="text-destructive border-destructive/30 hover:bg-destructive/10 rounded-lg" disabled={claimProcessing === claim.id} onClick={() => handleRejectClaim(claim.id)}>
                          <XCircle className="h-3 w-3 ml-1" />
                          رفض
                        </Button>
                      </div>
                    )}
                    {claim.status !== "pending" && (
                      <Badge variant={claim.status === "approved" ? "default" : "destructive"} className="text-xs">
                        {claim.status === "approved" ? "مقبول" : "مرفوض"}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );

  // =================== ADMIN USERS SECTION ===================
  const renderAdminUsers = () => (
    <>
      <SectionHeader title="إدارة المستخدمين (المشرف)" onBack={() => setActiveSection("home")} />
      
      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardContent className="py-12">
          <div className="text-center">
            <UserCog className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-amiri text-xl text-foreground mb-2">إدارة المشرفين</h3>
            <p className="text-muted-foreground text-sm">قريباً - سيتم إضافة إمكانية إدارة حسابات المشرفين</p>
          </div>
        </CardContent>
      </Card>
    </>
  );

  // =================== ADMIN PERMISSIONS SECTION ===================
  const renderAdminPermissions = () => (
    <>
      <SectionHeader title="صلاحيات المستخدمين (المشرف)" onBack={() => setActiveSection("home")} />
      
      <Card className="glass-card border-primary/10 rounded-2xl overflow-hidden">
        <CardContent className="py-12">
          <div className="text-center">
            <ShieldCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-amiri text-xl text-foreground mb-2">إدارة الصلاحيات</h3>
            <p className="text-muted-foreground text-sm">قريباً - سيتم إضافة إمكانية تخصيص صلاحيات المشرفين</p>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const handleApproveRequest = async (req: any) => {
    setRequestProcessing(req.id);
    try {
      const memberName = members.find(m => m.id === req.member_id)?.name || "عضو";
      
      if (req.request_type === "photo" || req.request_type === "video") {
        // Add to gallery
        await supabase.from("gallery_photos").insert({
          photo_url: req.data.url,
          title: req.data.title || null,
          description: req.data.description || null,
          member_id: req.member_id,
          category: req.request_type === "video" ? "video" : "general",
        });
      } else if (req.request_type === "business") {
        // Create business and link owner
        const { data: biz } = await supabase.from("family_businesses").insert({
          name_ar: req.data.name_ar,
          name_en: req.data.name_en || null,
          activity: req.data.activity || null,
          phone: req.data.phone || null,
          email: req.data.email || null,
          website: req.data.website || null,
          country: req.data.country || null,
          emirate: req.data.emirate || null,
          area: req.data.area || null,
          street: req.data.street || null,
        }).select("id").single();
        if (biz) {
          await supabase.from("business_owners").insert({ business_id: biz.id, member_id: req.member_id });
        }
      } else if (req.request_type === "child") {
        // Create child member and relationship
        const { data: child } = await supabase.from("family_members").insert({
          name: req.data.name,
          gender: req.data.gender || "male",
          birth_date: req.data.birth_date || null,
        }).select("id").single();
        if (child) {
          await supabase.from("family_relationships").insert({
            parent_id: req.member_id,
            child_id: child.id,
            relationship_type: "parent",
          });
        }
      }

      // Update request status
      await supabase.from("member_requests").update({
        status: "approved",
        reviewed_at: new Date().toISOString(),
        reviewed_by: user!.id,
      }).eq("id", req.id);

      toast({ title: "تمت الموافقة على الطلب" });
      fetchMembers();
    } catch (err: any) {
      toast({ title: "خطأ", description: err.message, variant: "destructive" });
    }
    setRequestProcessing(null);
  };

  const handleRejectRequest = async (reqId: string) => {
    setRequestProcessing(reqId);
    await supabase.from("member_requests").update({
      status: "rejected",
      reviewed_at: new Date().toISOString(),
      reviewed_by: user!.id,
    }).eq("id", reqId);
    toast({ title: "تم رفض الطلب" });
    fetchMembers();
    setRequestProcessing(null);
  };

  const requestTypeLabels: Record<string, string> = {
    photo: "📷 صورة",
    video: "🎬 فيديو",
    business: "🏢 شركة",
    child: "👶 إضافة ابن/بنت",
    edit_profile: "✏️ تعديل ملف شخصي",
    edit_child: "✏️ تعديل بيانات ابن",
    activation: "🔐 تفعيل حساب",
    personal_doc: "📄 وثيقة شخصية",
    public_doc: "📁 وثيقة عامة",
  };

  // Member Requests Sub-menu
  const requestsSubMenu = [
    { key: "requests-activation" as AdminSection, label: "طلب تفعيل حساب", icon: UserCheck, color: "text-green-500 bg-green-500/10", type: "activation" },
    { key: "requests-photo" as AdminSection, label: "طلب إضافة صورة", icon: Camera, color: "text-pink-500 bg-pink-500/10", type: "photo" },
    { key: "requests-business" as AdminSection, label: "طلب إضافة شركة", icon: Briefcase, color: "text-blue-500 bg-blue-500/10", type: "business" },
    { key: "requests-personal-doc" as AdminSection, label: "طلب إضافة وثيقة شخصية", icon: FileText, color: "text-violet-500 bg-violet-500/10", type: "personal_doc" },
    { key: "requests-public-doc" as AdminSection, label: "طلب إضافة وثيقة عامة", icon: FolderOpen, color: "text-amber-500 bg-amber-500/10", type: "public_doc" },
  ];

  const renderMemberRequests = () => {
    // Count pending requests by type
    const pendingCounts: Record<string, number> = {};
    memberRequests.filter(r => r.status === "pending").forEach(r => {
      pendingCounts[r.request_type] = (pendingCounts[r.request_type] || 0) + 1;
    });

    return (
      <>
        <SectionHeader title="طلبات الأعضاء" onBack={() => setActiveSection("home")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mt-6">
          {requestsSubMenu.map((item, i) => {
            const count = pendingCounts[item.type] || 0;
            return (
              <motion.div key={item.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <button
                  onClick={() => setActiveSection(item.key)}
                  className="w-full flex items-center gap-4 p-5 rounded-2xl glass-card border border-primary/10 hover:border-primary/30 transition-all hover:shadow-lg group text-right relative"
                  data-testid={`requests-${item.type}-btn`}
                >
                  <div className={`p-3 rounded-xl ${item.color} transition-transform group-hover:scale-110`}>
                    <item.icon className="h-6 w-6" />
                  </div>
                  <span className="font-medium text-foreground text-base flex-1">{item.label}</span>
                  {count > 0 && (
                    <span className="absolute top-3 left-3 min-w-[24px] h-6 flex items-center justify-center bg-destructive text-destructive-foreground text-xs font-bold rounded-full px-2">
                      {count}
                    </span>
                  )}
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors rotate-180" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </>
    );
  };

  // Render specific request type
  const renderRequestsByType = (requestType: string, title: string) => {
    const pendingReqs = memberRequests.filter(r => r.status === "pending" && r.request_type === requestType);
    const pastReqs = memberRequests.filter(r => r.status !== "pending" && r.request_type === requestType);

    return (
      <>
        <SectionHeader title={title} onBack={() => setActiveSection("member-requests")} />
        <Card className="glass-card border-primary/10 rounded-2xl">
          <CardHeader>
            <CardTitle className="font-amiri text-xl gold-text">
              طلبات بانتظار المراجعة ({pendingReqs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pendingReqs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">لا توجد طلبات معلّقة</p>
            ) : (
              <div className="space-y-3">
                {pendingReqs.map(req => {
                  const member = members.find(m => m.id === req.member_id);
                  return (
                    <div key={req.id} className="p-4 rounded-xl bg-secondary/30 border border-primary/10 space-y-2" data-testid={`request-card-${req.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium">{requestTypeLabels[req.request_type] || req.request_type}</span>
                          {member && <span className="text-xs text-muted-foreground mr-2">— {member.name}</span>}
                          {req.data?.email && <span className="text-xs text-primary mr-2">({req.data.email})</span>}
                        </div>
                        <span className="text-xs text-muted-foreground">{new Date(req.created_at).toLocaleDateString("ar-SA")}</span>
                      </div>
                      
                      {/* Show request details */}
                      <div className="text-xs text-muted-foreground bg-secondary/50 rounded-lg p-2 space-y-1">
                        {requestType === "activation" && (
                          <>
                            <p>البريد الإلكتروني: <span className="text-foreground" dir="ltr">{req.data?.email}</span></p>
                            <p>كود العائلة المستخدم: <span className="text-primary font-mono">{req.data?.family_code}</span></p>
                            {req.data?.member_name && <p>اسم العضو المرتبط: <span className="text-foreground">{req.data.member_name}</span></p>}
                          </>
                        )}
                        {requestType === "photo" && (
                          <>
                            {req.data?.title && <p>العنوان: <span className="text-foreground">{req.data.title}</span></p>}
                            {req.data?.url && (
                              <img src={req.data.url} alt="" className="w-20 h-20 object-cover rounded-lg mt-1" />
                            )}
                          </>
                        )}
                        {requestType === "business" && (
                          <>
                            <p>اسم الشركة: <span className="text-foreground">{req.data?.name_ar}</span></p>
                            {req.data?.activity && <p>النشاط: <span className="text-foreground">{req.data.activity}</span></p>}
                          </>
                        )}
                        {(requestType === "personal_doc" || requestType === "public_doc") && (
                          <>
                            <p>عنوان الوثيقة: <span className="text-foreground">{req.data?.title}</span></p>
                            {req.data?.description && <p>الوصف: <span className="text-foreground">{req.data.description}</span></p>}
                            {req.data?.file_url && (
                              <a href={req.data.file_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                معاينة الملف
                              </a>
                            )}
                          </>
                        )}
                      </div>

                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          className="rounded-xl bg-green-600 hover:bg-green-700 text-white"
                          disabled={requestProcessing === req.id}
                          onClick={() => handleApproveRequest(req)}
                          data-testid={`approve-request-${req.id}`}
                        >
                          {requestProcessing === req.id ? <Loader2 className="h-3 w-3 animate-spin ml-1" /> : <CheckCircle className="h-3 w-3 ml-1" />}
                          موافقة
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl border-destructive/30 text-destructive hover:bg-destructive/10"
                          disabled={requestProcessing === req.id}
                          onClick={() => handleRejectRequest(req.id)}
                          data-testid={`reject-request-${req.id}`}
                        >
                          <XCircle className="h-3 w-3 ml-1" />
                          رفض
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {pastReqs.length > 0 && (
          <Card className="glass-card border-primary/10 rounded-2xl mt-4">
            <CardHeader>
              <CardTitle className="font-amiri text-lg text-muted-foreground">الطلبات السابقة ({pastReqs.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pastReqs.slice(0, 20).map(req => {
                  const member = members.find(m => m.id === req.member_id);
                  return (
                    <div key={req.id} className="flex items-center justify-between p-2 rounded-lg bg-secondary/20">
                      <div className="flex items-center gap-2">
                        <span className="text-xs">{requestTypeLabels[req.request_type] || req.request_type}</span>
                        {member && <span className="text-xs text-muted-foreground">— {member.name}</span>}
                        {req.data?.email && <span className="text-xs text-muted-foreground">({req.data.email})</span>}
                      </div>
                      <Badge variant="secondary" className={`text-xs ${req.status === "approved" ? "text-green-500" : "text-destructive"}`}>
                        {req.status === "approved" ? "تمت الموافقة" : "مرفوض"}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </>
    );
  };

  const renderStatistics = () => (
    <>
      <SectionHeader title="إحصائيات" onBack={() => setActiveSection("home")} />
      
      {loading ? (
        <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6 max-w-lg mx-auto">
            {[
              { label: "إجمالي الأفراد", count: members.length, icon: Users, color: "text-primary" },
              { label: "الأحياء", count: members.filter(m => !m.is_deceased).length, icon: Heart, color: "text-green-500" },
              { label: "المتوفين", count: members.filter(m => m.is_deceased).length, icon: Cross, color: "text-muted-foreground" },
            ].map(s => (
              <Card key={s.label} className="glass-card border-primary/10 rounded-xl">
                <CardContent className="p-4 text-center">
                  <s.icon className={`h-5 w-5 mx-auto mb-1.5 ${s.color}`} />
                  <p className="text-2xl font-bold text-foreground">{s.count}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 max-w-xl mx-auto">
            {[
              { label: "أعزب/عزباء", value: "single", emoji: "💛" },
              { label: "متزوج/ة", value: "married", emoji: "💍" },
              { label: "مطلق/ة", value: "divorced", emoji: "💔" },
              { label: "أرمل/ة", value: "widowed", emoji: "🕊️" },
            ].map(s => (
              <Card key={s.value} className="glass-card border-primary/10 rounded-xl">
                <CardContent className="p-3 text-center">
                  <span className="text-lg">{s.emoji}</span>
                  <p className="text-xl font-bold text-foreground">{members.filter(m => m.marital_status === s.value).length}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
            {[
              { label: "ذكور", count: members.filter(m => m.gender === "male").length, emoji: "👨" },
              { label: "إناث", count: members.filter(m => m.gender === "female").length, emoji: "👩" },
            ].map(s => (
              <Card key={s.label} className="glass-card border-primary/10 rounded-xl">
                <CardContent className="p-4 text-center">
                  <span className="text-2xl">{s.emoji}</span>
                  <p className="text-2xl font-bold text-foreground mt-1">{s.count}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{s.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </>
  );

  // =================== BUSINESSES SECTION ===================
  const renderBusinesses = () => {
    const emptyBizForm = { name_ar: "", name_en: "", logo_url: "", activity: "", location: "", country: "الإمارات العربية المتحدة", emirate: "", area: "", street: "", email: "", phone: "", website: "", instagram: "", whatsapp: "", facebook: "", twitter_x: "" };


    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        setLogoCropImage(reader.result as string);
        setLogoCrop({ x: 0, y: 0 });
        setLogoZoom(1);
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    };

    const onLogoCropComplete = (_: any, croppedAreaPixels: any) => {
      setLogoCroppedAreaPixels(croppedAreaPixels);
    };

    const getCroppedImg = async (imageSrc: string, pixelCrop: any): Promise<Blob> => {
      const image = document.createElement("img");
      image.src = imageSrc;
      await new Promise(resolve => { image.onload = resolve; });
      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
      return new Promise(resolve => canvas.toBlob(blob => resolve(blob!), "image/png"));
    };

    const handleLogoCropSave = async () => {
      if (!logoCropImage || !logoCroppedAreaPixels) return;
      setBusinessLogoUploading(true);
      try {
        const blob = await getCroppedImg(logoCropImage, logoCroppedAreaPixels);
        const fileName = `logo-${Date.now()}-${Math.random().toString(36).slice(2)}.png`;
        const { error } = await supabase.storage.from("business-logos").upload(fileName, blob);
        if (error) {
          toast({ title: "خطأ في رفع اللوجو", description: error.message, variant: "destructive" });
        } else {
          const { data: urlData } = supabase.storage.from("business-logos").getPublicUrl(fileName);
          setBusinessForm(f => ({ ...f, logo_url: urlData.publicUrl }));
          toast({ title: "تم رفع اللوجو" });
        }
      } catch {
        toast({ title: "خطأ في قص الصورة", variant: "destructive" });
      }
      setBusinessLogoUploading(false);
      setLogoCropImage(null);
    };

    const handleBizSave = async () => {
      if (!businessForm.name_ar.trim()) {
        toast({ title: "اسم الشركة بالعربي مطلوب", variant: "destructive" });
        return;
      }
      setBusinessSaving(true);
      const activityStr = bizActivities.filter(a => a.trim()).join(" | ");
      const payload = { ...businessForm, activity: activityStr };
      let error;
      let bizId = businessEditId;
      if (businessEditId) {
        ({ error } = await supabase.from("family_businesses").update(payload).eq("id", businessEditId));
      } else {
        const { data, error: insertErr } = await supabase.from("family_businesses").insert(payload).select("id").single();
        error = insertErr;
        if (data) bizId = data.id;
      }
      
      // Save owners
      if (!error && bizId) {
        await supabase.from("business_owners").delete().eq("business_id", bizId);
        if (bizOwnerIds.length > 0) {
          const ownerRows = bizOwnerIds.map(mid => ({ business_id: bizId!, member_id: mid }));
          await supabase.from("business_owners").insert(ownerRows);
        }
      }

      setBusinessSaving(false);
      if (error) {
        toast({ title: "خطأ", description: error.message, variant: "destructive" });
      } else {
        toast({ title: businessEditId ? "تم التعديل" : "تمت الإضافة" });
        setBusinessOpen(false);
        setBusinessEditId(null);
        setBusinessForm(emptyBizForm);
        setBizOwnerIds([]);
        setBizActivities([""]);
        fetchMembers();
      }
    };

    const handleBizDelete = async (id: string) => {
      const { error } = await supabase.from("family_businesses").delete().eq("id", id);
      if (!error) { toast({ title: "تم حذف الشركة" }); fetchMembers(); }
    };

    const openEditBiz = async (b: any) => {
      setBusinessEditId(b.id);
      setBusinessForm({
        name_ar: b.name_ar || "", name_en: b.name_en || "", logo_url: b.logo_url || "",
        activity: b.activity || "", location: b.location || "",
        country: b.country || "الإمارات العربية المتحدة", emirate: b.emirate || "", area: b.area || "", street: b.street || "",
        email: b.email || "", phone: b.phone || "", website: b.website || "",
        instagram: b.instagram || "", whatsapp: b.whatsapp || "", facebook: b.facebook || "", twitter_x: b.twitter_x || "",
      });
      setBizActivities(b.activity ? b.activity.split(" | ") : [""]);
      // Load owners
      const { data: owners } = await supabase.from("business_owners").select("member_id").eq("business_id", b.id);
      setBizOwnerIds((owners || []).map((o: any) => o.member_id));
      setBusinessOpen(true);
    };

    return (
      <>
        <SectionHeader title="شركات العائلة" onBack={() => setActiveSection("home")} />
        <div className="flex gap-3 mb-6 justify-center">
          <Dialog open={businessOpen} onOpenChange={(o) => { setBusinessOpen(o); if (!o) { setBusinessEditId(null); setBusinessForm(emptyBizForm); } }}>
            <DialogTrigger asChild>
              <Button className="gold-gradient text-primary-foreground rounded-xl shadow-lg">
                <Plus className="h-4 w-4 ml-1" /> إضافة شركة
              </Button>
            </DialogTrigger>
            <DialogContent className="glass-card border-primary/20 max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl" dir="rtl">
              <DialogHeader>
                <DialogTitle className="font-amiri text-xl gold-text">{businessEditId ? "تعديل شركة" : "إضافة شركة جديدة"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                {/* Logo */}
                <div className="flex flex-col items-center gap-2">
                  {businessForm.logo_url && <img src={businessForm.logo_url} alt="logo" className="w-24 h-24 rounded-full object-cover border border-primary/20" />}
                  <Label className="cursor-pointer gold-gradient text-primary-foreground px-4 py-2 rounded-xl text-sm flex items-center gap-2">
                    <Upload className="h-4 w-4" /> {businessLogoUploading ? "جاري الرفع..." : "رفع اللوجو"}
                    <input type="file" accept="image/*" className="hidden" onChange={handleLogoSelect} disabled={businessLogoUploading} />
                  </Label>
                </div>
                {/* Crop Dialog */}
                {logoCropImage && (
                  <Dialog open={!!logoCropImage} onOpenChange={() => setLogoCropImage(null)}>
                    <DialogContent className="glass-card border-primary/20 max-w-md rounded-2xl" dir="rtl">
                      <DialogHeader><DialogTitle className="font-amiri gold-text text-center">قص اللوجو</DialogTitle></DialogHeader>
                      <div className="relative w-full h-72 bg-secondary/30 rounded-xl overflow-hidden">
                        <Cropper
                          image={logoCropImage}
                          crop={logoCrop}
                          zoom={logoZoom}
                          aspect={1}
                          cropShape="round"
                          onCropChange={setLogoCrop}
                          onZoomChange={setLogoZoom}
                          onCropComplete={onLogoCropComplete}
                        />
                      </div>
                      <div className="flex items-center gap-3 px-2">
                        <span className="text-xs text-muted-foreground">تكبير</span>
                        <input type="range" min={1} max={3} step={0.1} value={logoZoom} onChange={e => setLogoZoom(Number(e.target.value))} className="flex-1 accent-primary" />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" onClick={() => setLogoCropImage(null)} className="rounded-xl border-primary/20">إلغاء</Button>
                        <Button onClick={handleLogoCropSave} disabled={businessLogoUploading} className="gold-gradient rounded-xl">
                          {businessLogoUploading ? "جاري الرفع..." : "حفظ و رفع"}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
                <div><Label>اسم الشركة بالعربي *</Label><Input value={businessForm.name_ar} onChange={e => setBusinessForm(f => ({ ...f, name_ar: e.target.value }))} /></div>
                <div><Label>اسم الشركة بالانجليزي</Label><Input value={businessForm.name_en} onChange={e => setBusinessForm(f => ({ ...f, name_en: e.target.value }))} dir="ltr" /></div>
                {/* Multi-activity */}
                <div>
                  <Label>نشاط الشركة</Label>
                  {bizActivities.map((act, idx) => (
                    <div key={idx} className="flex gap-2 mt-1">
                      <Input value={act} onChange={e => { const n = [...bizActivities]; n[idx] = e.target.value; setBizActivities(n); }} placeholder={`نشاط ${idx + 1}`} />
                      {bizActivities.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-destructive shrink-0" onClick={() => setBizActivities(bizActivities.filter((_, i) => i !== idx))}><X className="h-4 w-4" /></Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" className="mt-1 text-primary" onClick={() => setBizActivities([...bizActivities, ""])}><Plus className="h-3 w-3 ml-1" /> إضافة نشاط آخر</Button>
                </div>
                {/* Owners */}
                <div>
                  <Label>أصحاب الشركة (أفراد العائلة)</Label>
                  {bizOwnerIds.map((oid, idx) => (
                    <div key={idx} className="flex gap-2 mt-1">
                      <div className="flex-1"><SearchableMemberSelect members={members} value={oid} onChange={v => { const n = [...bizOwnerIds]; n[idx] = v; setBizOwnerIds(n); }} placeholder="ابحث واختر فرد..." /></div>
                      <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-destructive shrink-0" onClick={() => setBizOwnerIds(bizOwnerIds.filter((_, i) => i !== idx))}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" className="mt-1 text-primary" onClick={() => setBizOwnerIds([...bizOwnerIds, ""])}><Plus className="h-3 w-3 ml-1" /> إضافة صاحب شركة</Button>
                </div>
                <div className="border-t border-primary/10 pt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-3">عنوان الشركة</p>
                  <div className="space-y-3">
                    <div><Label>الدولة</Label><Input value={businessForm.country} onChange={e => setBusinessForm(f => ({ ...f, country: e.target.value }))} /></div>
                    <div><Label>الإمارة</Label><Input value={businessForm.emirate} onChange={e => setBusinessForm(f => ({ ...f, emirate: e.target.value }))} placeholder="مثال: الشارقة" /></div>
                    <div><Label>المنطقة</Label><Input value={businessForm.area} onChange={e => setBusinessForm(f => ({ ...f, area: e.target.value }))} placeholder="مثال: المجاز" /></div>
                    <div><Label>الشارع</Label><Input value={businessForm.street} onChange={e => setBusinessForm(f => ({ ...f, street: e.target.value }))} placeholder="مثال: شارع الوحدة" /></div>
                  </div>
                </div>
                <div><Label>رابط الموقع على الخريطة</Label><Input value={businessForm.location} onChange={e => setBusinessForm(f => ({ ...f, location: e.target.value }))} dir="ltr" placeholder="https://maps.google.com/..." /></div>
                <div><Label>البريد الإلكتروني</Label><Input type="email" value={businessForm.email} onChange={e => setBusinessForm(f => ({ ...f, email: e.target.value }))} dir="ltr" /></div>
                <div><Label>رقم التواصل</Label><Input value={businessForm.phone} onChange={e => setBusinessForm(f => ({ ...f, phone: e.target.value }))} dir="ltr" /></div>
                <div><Label>الموقع الإلكتروني</Label><Input value={businessForm.website} onChange={e => setBusinessForm(f => ({ ...f, website: e.target.value }))} dir="ltr" placeholder="https://" /></div>
                <div className="border-t border-primary/10 pt-4">
                  <p className="text-sm font-medium text-muted-foreground mb-3">وسائل التواصل الاجتماعي</p>
                  <div className="space-y-3">
                    <div><Label>انستغرام</Label><Input value={businessForm.instagram} onChange={e => setBusinessForm(f => ({ ...f, instagram: e.target.value }))} dir="ltr" placeholder="@username أو رابط" /></div>
                    <div><Label>واتساب</Label><Input value={businessForm.whatsapp} onChange={e => setBusinessForm(f => ({ ...f, whatsapp: e.target.value }))} dir="ltr" placeholder="رقم الواتساب" /></div>
                    <div><Label>فيس بوك</Label><Input value={businessForm.facebook} onChange={e => setBusinessForm(f => ({ ...f, facebook: e.target.value }))} dir="ltr" placeholder="رابط الصفحة" /></div>
                    <div><Label>اكس (تويتر)</Label><Input value={businessForm.twitter_x} onChange={e => setBusinessForm(f => ({ ...f, twitter_x: e.target.value }))} dir="ltr" placeholder="@username أو رابط" /></div>
                  </div>
                </div>
                <Button onClick={handleBizSave} disabled={businessSaving} className="w-full gold-gradient text-primary-foreground rounded-xl">
                  {businessSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : (businessEditId ? "تحديث" : "إضافة")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {businesses.length === 0 ? (
          <p className="text-center text-muted-foreground py-10">لا توجد شركات مضافة</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {businesses.map(b => (
              <Card key={b.id} className="glass-card border-primary/10 rounded-2xl overflow-hidden">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt={b.name_ar} className="w-16 h-16 rounded-xl object-contain border border-primary/10" />
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-primary/5 flex items-center justify-center"><Building2 className="h-8 w-8 text-primary/30" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-foreground text-lg">{b.name_ar}</h3>
                      {b.name_en && <p className="text-sm text-muted-foreground" dir="ltr">{b.name_en}</p>}
                      {b.activity && <p className="text-sm text-muted-foreground mt-1">{b.activity}</p>}
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                        {b.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{b.location}</span>}
                        {b.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{b.phone}</span>}
                        {b.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{b.email}</span>}
                        {b.website && <a href={b.website} target="_blank" rel="noopener" className="flex items-center gap-1 text-primary hover:underline"><Globe className="h-3 w-3" />الموقع</a>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEditBiz(b)} className="rounded-xl h-8 w-8"><Edit className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => handleBizDelete(b.id)} className="rounded-xl h-8 w-8 text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </>
    );
  };

  // =================== SOCIAL MEDIA SECTION ===================
  const socialSubMenu = [
    { key: "social-channels" as AdminSection, label: "التحكم بقنوات التواصل الاجتماعي للموقع", icon: Share2, color: "text-sky-500 bg-sky-500/10" },
    { key: "social-publish" as AdminSection, label: "التحكم بالنشر في قنوات التواصل الاجتماعي", icon: Newspaper, color: "text-amber-500 bg-amber-500/10" },
  ];

  const renderSocialMedia = () => {
    return (
      <>
        <SectionHeader title="قنوات التواصل الاجتماعي" onBack={() => setActiveSection("home")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mt-6">
          {socialSubMenu.map((item, i) => (
            <motion.div key={item.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <button
                onClick={() => setActiveSection(item.key)}
                className="w-full flex items-center gap-4 p-5 rounded-2xl glass-card border border-primary/10 hover:border-primary/30 transition-all hover:shadow-lg group text-right"
              >
                <div className={`p-3 rounded-xl ${item.color} transition-transform group-hover:scale-110`}>
                  <item.icon className="h-6 w-6" />
                </div>
                <span className="font-medium text-foreground text-base flex-1">{item.label}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors rotate-180" />
              </button>
            </motion.div>
          ))}
        </div>
      </>
    );
  };

  const renderSocialChannels = () => {
    const fixedChannels = [
      { key: "whatsapp", label: "واتساب", icon: "💬" },
      { key: "instagram", label: "انستغرام", icon: "📷" },
      { key: "facebook", label: "فيس بوك", icon: "📘" },
      { key: "twitter_x", label: "اكس (تويتر)", icon: "🐦" },
    ];


    const handleChannelSave = async (platform: string) => {
      const entry = channelUrls[platform];
      setChannelSaving(platform);
      let error;
      if (entry.id) {
        // Update existing
        ({ error } = await supabase.from("social_media_channels").update({ url: entry.url }).eq("id", entry.id));
      } else if (entry.url.trim()) {
        // Insert new
        ({ error } = await supabase.from("social_media_channels").insert({ platform, url: entry.url, label: null }));
      }
      setChannelSaving(null);
      if (error) {
        toast({ title: "خطأ", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "تم الحفظ" });
        fetchMembers();
      }
    };

    return (
      <>
        <SectionHeader title="التحكم بقنوات التواصل الاجتماعي للموقع" onBack={() => setActiveSection("social-media")} />
        <div className="max-w-xl mx-auto space-y-4 mt-4">
          {fixedChannels.map(ch => (
            <Card key={ch.key} className="glass-card border-primary/10 rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-2xl">{ch.icon}</span>
                  <span className="font-medium text-foreground text-base">{ch.label}</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={channelUrls[ch.key]?.url || ""}
                    onChange={e => setChannelUrls(prev => ({ ...prev, [ch.key]: { ...prev[ch.key], url: e.target.value } }))}
                    dir="ltr"
                    placeholder="الرابط أو المعرّف"
                    className="flex-1 bg-secondary/50 border-primary/20 rounded-xl"
                  />
                  <Button
                    onClick={() => handleChannelSave(ch.key)}
                    disabled={channelSaving === ch.key}
                    className="gold-gradient text-primary-foreground rounded-xl"
                    size="sm"
                  >
                    {channelSaving === ch.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </>
    );
  };

  const renderSocialPublish = () => {
    return (
      <>
        <SectionHeader title="التحكم بالنشر في قنوات التواصل الاجتماعي" onBack={() => setActiveSection("social-media")} />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="p-6 rounded-full bg-primary/10 mb-6">
            <Newspaper className="h-12 w-12 text-primary" />
          </div>
          <h3 className="font-amiri text-xl font-bold text-primary mb-2">قريباً</h3>
          <p className="text-muted-foreground max-w-md">سيتم إضافة خاصية النشر المباشر على قنوات التواصل الاجتماعي قريباً إن شاء الله</p>
        </div>
      </>
    );
  };

  // =================== DOCUMENTS SECTION ===================
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setDocumentsUploading(true);
    const ext = file.name.split(".").pop() || "pdf";
    const path = `documents/${Date.now()}.${ext}`;
    
    const { error } = await supabase.storage.from("family-files").upload(path, file);
    if (error) {
      toast({ title: "خطأ في رفع الملف", description: error.message, variant: "destructive" });
      setDocumentsUploading(false);
      return;
    }
    
    const { data: urlData } = supabase.storage.from("family-files").getPublicUrl(path);
    setDocumentsForm(prev => ({ ...prev, file_url: urlData.publicUrl }));
    setDocumentsUploading(false);
    toast({ title: "تم رفع الملف بنجاح" });
  };

  const handleDocumentSave = async () => {
    if (!documentsForm.title || !documentsForm.file_url) {
      toast({ title: "يرجى تعبئة جميع الحقول المطلوبة", variant: "destructive" });
      return;
    }
    
    setDocumentsSaving(true);
    
    const docData = {
      title: documentsForm.title,
      description: documentsForm.description || null,
      file_url: documentsForm.file_url,
      doc_type: documentsForm.doc_type,
      member_id: documentsForm.doc_type === "personal" && documentsForm.member_id ? documentsForm.member_id : null,
    };
    
    let error;
    if (documentsEditId) {
      ({ error } = await supabase.from("family_documents").update(docData).eq("id", documentsEditId));
    } else {
      ({ error } = await supabase.from("family_documents").insert(docData));
    }
    
    setDocumentsSaving(false);
    
    if (error) {
      toast({ title: "خطأ في الحفظ", description: error.message, variant: "destructive" });
    } else {
      toast({ title: documentsEditId ? "تم التحديث بنجاح" : "تم الإضافة بنجاح" });
      setDocumentsOpen(false);
      setDocumentsForm({ title: "", description: "", file_url: "", doc_type: "public", member_id: "" });
      setDocumentsEditId(null);
      fetchMembers();
    }
  };

  const handleDocumentDelete = async (id: string) => {
    if (!confirm("هل أنت متأكد من حذف هذا الملف؟")) return;
    const { error } = await supabase.from("family_documents").delete().eq("id", id);
    if (error) {
      toast({ title: "خطأ في الحذف", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "تم الحذف" });
      fetchMembers();
    }
  };

  const openEditDocument = (doc: any) => {
    setDocumentsForm({
      title: doc.title,
      description: doc.description || "",
      file_url: doc.file_url,
      doc_type: doc.doc_type,
      member_id: doc.member_id || "",
    });
    setDocumentsEditId(doc.id);
    setDocumentsOpen(true);
  };

  const renderDocuments = () => {
    const publicDocsCount = documents.filter(d => d.doc_type === "public").length;
    const personalDocsCount = documents.filter(d => d.doc_type === "personal").length;

    const documentsSubMenu = [
      { key: "documents-public" as AdminSection, label: "وثائق عامة", icon: FolderOpen, color: "text-amber-500 bg-amber-500/10", count: publicDocsCount },
      { key: "documents-personal" as AdminSection, label: "وثائق شخصية", icon: File, color: "text-violet-500 bg-violet-500/10", count: personalDocsCount },
    ];
    
    return (
      <>
        <SectionHeader title="وثائق العائلة" onBack={() => setActiveSection("home")} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto mt-8">
          {documentsSubMenu.map((item, i) => (
            <motion.div key={item.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <button
                onClick={() => setActiveSection(item.key)}
                className="w-full flex flex-col items-center gap-4 p-8 rounded-2xl glass-card border border-primary/10 hover:border-primary/30 transition-all hover:shadow-lg hover:shadow-primary/5 group"
                data-testid={`docs-${item.key}-btn`}
              >
                <div className={`p-5 rounded-2xl ${item.color} transition-transform group-hover:scale-110`}>
                  <item.icon className="h-10 w-10" />
                </div>
                <span className="font-amiri text-xl font-bold text-foreground">{item.label}</span>
                <span className="text-sm text-muted-foreground">
                  {item.count} {item.count === 1 ? "وثيقة" : "وثائق"}
                </span>
              </button>
            </motion.div>
          ))}
        </div>
      </>
    );
  };

  // Render documents by type (public or personal)
  const renderDocumentsByType = (docType: "public" | "personal", title: string) => {
    const filteredDocs = documents.filter(d => d.doc_type === docType);
    
    return (
      <>
        <SectionHeader title={title} onBack={() => setActiveSection("documents")} />
        
        {/* Add Button */}
        <div className="flex justify-end mb-6">
          <Button 
            onClick={() => { 
              setDocumentsForm({ title: "", description: "", file_url: "", doc_type: docType, member_id: "" }); 
              setDocumentsEditId(null);
              setDocumentsOpen(true); 
            }} 
            className="gold-gradient text-primary-foreground rounded-xl"
            data-testid="add-document-btn"
          >
            <Plus className="h-4 w-4 ml-2" />
            إضافة وثيقة {docType === "personal" ? "شخصية" : "عامة"}
          </Button>
        </div>

        {/* Documents Grid */}
        {filteredDocs.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="h-20 w-20 text-primary/10 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">لا توجد وثائق {docType === "personal" ? "شخصية" : "عامة"} حالياً</p>
            <p className="text-muted-foreground/60 text-sm mt-2">اضغط على زر الإضافة لإضافة وثيقة جديدة</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocs.map((doc) => {
              const memberName = doc.member_id ? members.find(m => m.id === doc.member_id)?.name : null;
              return (
                <Card key={doc.id} className="glass-card border-primary/10 rounded-2xl overflow-hidden" data-testid={`document-card-${doc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-3 rounded-xl ${docType === "personal" ? "bg-violet-500/10 text-violet-500" : "bg-amber-500/10 text-amber-500"} shrink-0`}>
                        {docType === "personal" ? <File className="h-6 w-6" /> : <FolderOpen className="h-6 w-6" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{doc.title}</h3>
                        {doc.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{doc.description}</p>
                        )}
                        {memberName && (
                          <p className="text-xs text-primary mt-2 flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {memberName}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground/60 mt-2">
                          {new Date(doc.created_at).toLocaleDateString("ar-SA")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-primary/10">
                      <a 
                        href={doc.file_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        <Download className="h-4 w-4" />
                        تحميل
                      </a>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEditDocument(doc)} className="rounded-xl h-8 w-8">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDocumentDelete(doc.id)} className="rounded-xl h-8 w-8 text-destructive">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={documentsOpen} onOpenChange={setDocumentsOpen}>
          <DialogContent className="max-w-md rounded-2xl glass-card border-primary/20">
            <DialogHeader>
              <DialogTitle className="font-amiri text-xl gold-text">
                {documentsEditId ? "تعديل وثيقة" : `إضافة وثيقة ${docType === "personal" ? "شخصية" : "عامة"}`}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">عنوان الوثيقة *</label>
                <Input
                  value={documentsForm.title}
                  onChange={e => setDocumentsForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="مثال: صك ملكية..."
                  className="bg-secondary/50 border-primary/20 rounded-xl"
                  data-testid="doc-title-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1.5 block">الوصف (اختياري)</label>
                <Input
                  value={documentsForm.description}
                  onChange={e => setDocumentsForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="وصف مختصر للوثيقة..."
                  className="bg-secondary/50 border-primary/20 rounded-xl"
                  data-testid="doc-desc-input"
                />
              </div>
              
              {docType === "personal" && (
                <div>
                  <label className="text-sm font-medium mb-1.5 block">صاحب الوثيقة</label>
                  <SearchableMemberSelect
                    members={members}
                    value={documentsForm.member_id}
                    onChange={(v) => setDocumentsForm(prev => ({ ...prev, member_id: v }))}
                    placeholder="اختر فرد من العائلة..."
                  />
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium mb-1.5 block">ملف الوثيقة *</label>
                <div className="flex items-center gap-3">
                  <label className="flex-1 cursor-pointer">
                    <div className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed transition-colors ${documentsForm.file_url ? 'border-primary/50 bg-primary/5' : 'border-primary/20 hover:border-primary/40'}`}>
                      {documentsUploading ? (
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      ) : (
                        <>
                          <Upload className="h-5 w-5 text-primary/60" />
                          <span className="text-sm text-muted-foreground">
                            {documentsForm.file_url ? "تم رفع الملف ✓" : "اضغط لرفع ملف"}
                          </span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                      onChange={handleDocumentUpload}
                      className="hidden"
                      data-testid="doc-file-input"
                    />
                  </label>
                </div>
                {documentsForm.file_url && (
                  <a href={documentsForm.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-2 inline-block">
                    معاينة الملف
                  </a>
                )}
              </div>
              
              <Button 
                onClick={handleDocumentSave} 
                disabled={documentsSaving || !documentsForm.title || !documentsForm.file_url}
                className="w-full gold-gradient text-primary-foreground rounded-xl mt-4"
                data-testid="save-document-btn"
              >
                {documentsSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : (documentsEditId ? "تحديث" : "إضافة")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case "members": return renderMembers();
      case "relationships": return renderRelationships();
      case "news": return renderNews();
      case "codes": return renderCodes();
      case "gallery": return renderGallery();
      case "businesses": return renderBusinesses();
      case "documents": return renderDocuments();
      case "documents-public": return renderDocumentsByType("public", "الوثائق العامة");
      case "documents-personal": return renderDocumentsByType("personal", "الوثائق الشخصية");
      case "social-media": return renderSocialMedia();
      case "social-channels": return renderSocialChannels();
      case "social-publish": return renderSocialPublish();
      case "family-users": return renderFamilyUsers();
      case "admin-users": return renderAdminUsers();
      case "admin-permissions": return renderAdminPermissions();
      case "statistics": return renderStatistics();
      case "member-requests": return renderMemberRequests();
      case "requests-activation": return renderRequestsByType("activation", "طلبات تفعيل الحسابات");
      case "requests-photo": return renderRequestsByType("photo", "طلبات إضافة الصور");
      case "requests-business": return renderRequestsByType("business", "طلبات إضافة الشركات");
      case "requests-personal-doc": return renderRequestsByType("personal_doc", "طلبات إضافة وثائق شخصية");
      case "requests-public-doc": return renderRequestsByType("public_doc", "طلبات إضافة وثائق عامة");
      default: return renderHome();
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-5xl">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-8">
          <h1 className="font-amiri text-4xl font-bold gold-text mb-2 flex items-center justify-center gap-3">
            <Shield className="h-8 w-8 text-primary" />
            لوحة تحكم المشرف
          </h1>
          <div className="w-24 h-[2px] gold-gradient rounded-full mx-auto mt-4" />
        </motion.div>

        {renderSection()}
      </div>
    </div>
  );
};

export default Admin;
